import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { FailureApi, IFailureApiCodes } from "@smp/api";
import { Failure, IFailureCodes } from "@smp/utils";
import { useCallback, useMemo } from "react";

import { isGqlError } from "@/utils";

const getPreferedApiError = (payload: FailureApi["payload"]) =>
  ({
    INSTAGRAM_BUSINESS_ACCOUNT_CANT_BE_PRIVATE: t`A business Account cannot be closed`,
    INSTAGRAM_ACCOUNT_CONFIRMATION_REQUIRED: t`Account confirmation required`,
    INSTAGRAM_CREATOR_ACCOUNT_CANT_BE_PRIVATE: t`The author's account cannot be closed`,
    ACTION_BLOCKED: t`Too many requests. Please try again later`,
  })[payload.body.error_type];

export const useFailureTranslator = () => {
  const { i18n } = useLingui();

  const failureTranslationGet = useCallback(
    (failure: unknown) => {
      if (failure instanceof FailureApi) {
        const code = failure.code;
        const payload = failure.payload;

        const messages: Record<IFailureApiCodes, string> = {
          /**
           * ep.billing
           */
          API__EP__BILLING__AFFILIATE_CUSTOM_CODE_ALREADY_EXISTS: i18n._(t`Affiliate code already exists`),
          API__EP__BILLING__BILLING_POLICY_INCOMPATIBLE: i18n._(
            t`These actions are not supported by your billing policy.`,
          ),
          API__EP__BILLING__BILL_PRODUCT_FORBIDDEN: i18n._(t`Current user cannot purchase this product.`),
          API__EP__BILLING__BILL_PRODUCT_PRICE_ZERO: i18n._(t`Unable to pay for product: price is equal to zero.`),
          API__EP__BILLING__BILL_PRODUCT_SALE_FROZEN: i18n._(t`Sales information is currently frozen.`),
          API__EP__BILLING__LIMIT_HIT_LIVE_STREAMS_CONFIGURED: i18n._(
            t`Sorry, you have reached the limit of live-stream configs.`,
          ),
          API__EP__BILLING__PLANSUBSCRIPTION__AUTOAPPROVE_LIMIT_HIT: i18n._(
            t`Sorry, you cannot switch to the selected plan, as you have turned on Auto-accept for too many Instagram accounts.`,
          ),
          API__EP__BILLING__PLANSUBSCRIPTION__ORGANIZATION_LIMIT_HIT: i18n._(
            t`Sorry, you cannot switch to the selected plan, as you manage too many teams.`,
          ),
          API__EP__BILLING__PLANSUBSCRIPTION__ORGANIZATION_MEMBERSHIP_LIMIT_HIT: i18n._(
            t`Sorry, you cannot switch to the selected plan, as there are too many members in one of your teams.`,
          ),
          API__EP__BILLING__PLANSUBSCRIPTION__PAGES_LIMIT_HIT: i18n._(
            t`Sorry, you cannot switch to the selected plan, as your scheduled posts are spread across too many social pages.`,
          ),
          API__EP__BILLING__PLAN_ACTIVE_INCOMPATIBLE: i18n._(
            t`Unable to perform this action with current active plan subscription. Please, convert your active plan and try again.`,
          ),
          API__EP__BILLING__PLAN_CONVERT_INSUFFICIENT_SOURCE_PERIOD: i18n._(
            t`Remaining Subscription period is insufficient for conversion into selected Plan.`,
          ),
          API__EP__BILLING__PLAN_CONVERT_SOURCE_INVALID: i18n._(
            t`Couldn't find the Plan for conversion, or the product is free.`,
          ),
          API__EP__BILLING__PLAN_CONVERT_TARGET_INVALID: i18n._(
            t`Couldn't find the Plan for conversion, or the product is free.`,
          ),
          API__EP__BILLING__PLAN_SUBSCRIPTION_ACTIVE_REQUIRED: i18n._(t`Active plan subscription required.`),
          API__EP__BILLING__POLICY__BILLING_POLICY_INCOMPATIBLE: i18n._(
            t`These actions are not supported by your billing policy.`,
          ),
          API__EP__BILLING__POLICY__RECURRING_PAYMENT_BILLING_POLICY_LOCKED: i18n._(
            t`This action requires switching your billing policy, which is forbidden while you have a Recurring Payment (subscription) in effect. Please cancel your Recurring Payments before this action and retry.`,
          ),
          API__EP__BILLING__POST_LIMIT_HIT: i18n._(t`Scheduling limit exceeded.`),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_BILL_PRODUCT_FORBIDDEN: i18n._(
            t`Current user cannot purchase this product.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_BILL_PRODUCT_PRICE_ZERO: i18n._(
            t`Unable to pay for product: price is equal to zero.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_BILL_PRODUCT_RECURRING_NOT_ALLOWED: i18n._(
            t`Recurring payment for product is not allowed.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_BILL_PRODUCT_SALE_FROZEN: i18n._(
            t`Sales information is currently frozen.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_BILL_PRODUCT_SINGLE_NOT_ALLOWED: i18n._(
            t`This product cannot be purchased.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_INTERNAL_FUNDS_NOT_ALLOWED: i18n._(
            t`You cannot use Account funds for this action.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_PLAN_ACTIVE_INCOMPATIBLE: i18n._(
            t`Unable to perform this action with current active plan subscription. Please, convert your active plan and try again.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_PLAN_FREE_FORBIDDEN: i18n._(
            t`Free/Trial Subscriptions are not available while you has active plan.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_PLAN_HAS_NO_TRIAL: i18n._(t`Selected Product has no trial version.`),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_PLAN_TRIAL_ALREADY_TRIED: i18n._(
            t`You have already used the Trial version, it is no longer available.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_PLAN_TRIAL_REQUIRE_RECURRING_PAYMENT: i18n._(
            t`Trial plan subscription cannot be purchased through a one-time payment. Please activate recurring payments to start the trial.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_RECURRING_PAYMENT_ACTIVE_EXISTS: i18n._(
            t`You already have an active Recurring Payment Subscription.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_RECURRING_PAYMENT_BILLING__PRODUCT__PLAN__PRODUCT_POLICY_LOCKED:
            i18n._(
              t`This action requires switching your billing policy, which is forbidden while you have a Recurring Payment (subscription) in effect. Please cancel your Recurring Payments before this action and retry.`,
            ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_RECURRING_PAYMENT_FLOOD: i18n._(
            t`You are already creating another Recurring Payment at this moment. You cannot be creating more than one Recurring Payment at a time.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_RECURRING_PAYMENT_PLAN_LOCKED: i18n._(
            t`Unable to change your billing plan while a Recurring Payment (Subscription) is in effect for your current plan. In order to change your billing plan you need to cancel the Recurring Payments.`,
          ),
          API__EP__BILLING__PRODUCT__PLAN__PRODUCT_RECURRING_PAYMENT_PROVIDER_INCOMPATIBLE: i18n._(
            t`Currency or price is not supported by the Payment Provider.`,
          ),

          API__EP__BILLING__PROMOCODE_NOT_FOUND: i18n._(t`Promo code not found.`),
          API__EP__BILLING__PROMOCODE_OUT_OF_SEASON: i18n._(t`Promo code is invalid: out of season.`),
          API__EP__BILLING__PROMOCODE_PRODUCT_INCOMPATIBLE: i18n._(
            t`Promo code was not used, as related product cannot be purchased.`,
          ),
          API__EP__BILLING__PROMOCODE_REDEEMED: i18n._(t`This promo code has already been activated.`),
          API__EP__BILLING__PROMOCODE_REDEEMS_EXCEEDED: i18n._(t`Promo code is invalid: redemption limit reached.`),
          API__EP__BILLING__RECURRING_PAYMENT_FREE_PLAN_LOCKED: i18n._(
            t`Unable to deactivate your free billing plan while you have an active Recurring Payment Subscription. In order to deactivate your free billing plan you need to cancel the Recurring Payment Subscription.`,
          ),
          API__EP__BILLING__RECURRING_PAYMENT_PLAN_LOCKED: i18n._(
            t`Unable to change your billing plan while a Recurring Payment (Subscription) is in effect for your current plan. In order to change your billing plan you need to cancel the Recurring Payments.`,
          ),
          API__EP__BILLING__RECURRING_PAYMENT_TRIAL_PLAN_LOCKED: i18n._(
            t`Unable to deactivate your trial billing plan while you have an active Recurring Payment Subscription. In order to deactivate your trial billing plan you need to cancel the Recurring Payment Subscription.`,
          ),
          /**
           * ep.organization
           */
          API__EP__ORGANIZATION__INVITE_ALREADY_CONFIRMED: i18n._(t`This invitation has already been confirmed`),
          API__EP__ORGANIZATION__INVITE_CANCELLED: i18n._(t`Team invitation has been canceled`),
          API__EP__ORGANIZATION__INVITE_CODE_INVALID: i18n._(t`Invitation code is invalid`),
          API__EP__ORGANIZATION__INVITE_CODE_OUT_OF_DATE: i18n._(t`Invitation code is out of date`),
          API__EP__ORGANIZATION__INVITE_USER_MISMATCH: i18n._(t`This invitation is for a different user`),
          API__EP__ORGANIZATION__ORGANIZATION_ID_INVALID: i18n._(t`Invalid Team ID`),
          API__EP__ORGANIZATION__ORGANIZATION_LIMIT_HIT: i18n._(t`Forbidden to create new team (Plan limit reached).`),
          API__EP__ORGANIZATION__ORGANIZATION_MEMBERSHIP_LIMIT_HIT: i18n._(
            t`Forbidden to invite new team members (Plan limit reached).`,
          ),
          API__EP__ORGANIZATION__ORGANIZATION_NAME_ALREADY_EXISTS: i18n._(t`Team name already exists`),
          API__EP__ORGANIZATION__ORGANIZATION_NOT_EMPTY: i18n._(
            t`Forbidden to delete non-empty teams. Please remove all dependent Social Sets from the team, and try again.`,
          ),
          API__EP__ORGANIZATION__ORGANIZATION_NOT_FOUND: i18n._(
            t`Team not found. You may not be a confirmed team member, or the team may have been deleted.`,
          ),
          API__EP__ORGANIZATION__ORGANIZATION_TEMPORARILY_INACTIVE: i18n._(
            t`Team is temporarily inactive. Contact the team owner to get more information or check your plan subscription.`,
          ),
          API__EP__ORGANIZATION__SELF_INVITE_FORBIDDEN: i18n._(t`You can only invite other users to the team.`),
          API__EP__ORGANIZATION__USER_EMAIL_NOT_VERIFIED: i18n._(t`Only verified users can be invited to a team.`),
          /**
           * ep.page_config
           */
          API__EP__PAGE_CONFIG__ACCOUNT_CREDENTIAL_NOT_CONNECTED: i18n._(
            t`Linked account does not belong to organization or user.`,
          ),
          API__EP__PAGE_CONFIG__ACCOUNT_CREDENTIAL_RECONNECT_REQUIRED: i18n._(
            t`The connection to the linked account is obsolete. Please reconnect the account.`,
          ),
          API__EP__PAGE_CONFIG__ACTION_BLOCKED: i18n._(t`This action has been blocked`),
          API__EP__PAGE_CONFIG__ORGANIZATION_PAGE_CONFIG_DESTROY_FORBIDDEN: i18n._(t`These actions are forbidden`),
          API__EP__PAGE_CONFIG__PAGECONFIG_COPY_FORBIDDEN: i18n._(
            t`User must have a Page Config for an equivalent Page to create a new Page Config.`,
          ),
          API__EP__PAGE_CONFIG__PAGECONFIG_MANAGE_CONFLICT: i18n._(
            t`Equivalent social profile is already in the process of being connected/disconnected, please try again later.`,
          ),
          API__EP__PAGE_CONFIG__PAGE_CONFIG_NAME_ALREADY_EXISTS: i18n._(
            t`«name» must be unique for each Social Profile on Page.`,
          ),
          API__EP__PAGE_CONFIG__PAGE_CONFIG_TRANSFER_PAGE_CONFIG_INVALID: i18n._(
            t`You must select another copy of this Social Profile to transfer`,
          ),
          API__EP__PAGE_CONFIG__UNKNOWN: i18n._(t`Unknown error has occurred.`),
          /**
           * ep.post
           */
          API__EP__POST__ACTION_BLOCKED: i18n._(t`This action is blocked`),
          API__EP__POST__EMAIL_NOT_VERIFIED: i18n._(t`Please verify your e-mail address before this action`),
          API__EP__POST__PAGES_LIMIT_HIT: i18n._(t`Page limit reached`),
          API__EP__POST__PLAN_SUBSCRIPTION_ENDED: i18n._(t`Plan subscription has expired`),
          API__EP__POST__POST_ALREADY_EXISTS: i18n._(t`Post already exists`),
          API__EP__POST__POST_IMMUTABLE_WHEN_COMPLETED: i18n._(t`You cannot edit completed post`),
          API__EP__POST__POST_IMMUTABLE_WHEN_FAILED: i18n._(t`You cannot edit failed post`),
          API__EP__POST__POST_IMMUTABLE_WHEN_LOCKED: i18n._(t`You cannot edit locked post`),
          API__EP__POST__POST_LIMIT_HIT: i18n._(t`Post limit has been reached`),
          /**
           * ep.sauth
           */
          API__EP__SAUTH__FORBIDDEN_FOR_MEDIUM: i18n._(t`Current action is prohibited for this social network`),
          API__EP__SAUTH__FORBIDDEN_FOR_USER: i18n._(t`Current action is forbidden for this user`),
          API__EP__SAUTH__MULTIPLE_LOGIN: i18n._(t`Account has already been used by another user`),
          API__EP__SAUTH__UNKNOWN_ACCOUNT: i18n._(t`Social profile not recognized`),
          /**
           * ep.social_page_group
           */
          API__EP__SOCIAL_PAGE_GROUP__ACTION_BLOCKED: i18n._(t`This action has been blocked`),
          API__EP__SOCIAL_PAGE_GROUP__ORGANIZATION_TEMPORARILY_INACTIVE: i18n._(
            t`Team is temporarily inactive. Contact the team owner to get more information or check your plan subscription.`,
          ),
          API__EP__SOCIAL_PAGE_GROUP__SOCIAL_PAGE_GROUPS_LIMIT_HIT: i18n._(t`Social Set limit exceeded.`),
          API__EP__SOCIAL_PAGE_GROUP__SOCIAL_PAGE_GROUP_NAME_ALREADY_EXISTS: i18n._(
            t`«name» must be unique for each Social Set.`,
          ),
          API__EP__SOCIAL_PAGE_GROUP__UNKNOWN: i18n._(t`Unknown error has occurred.`),
          /**
           * ep.story
           */
          API__EP__STORY__BILLING_DENY: i18n._(t`This operation is prohibited for current billing policy.`),
          API__EP__STORY__PAGES_LIMIT_HIT: i18n._(t`Social Profile limit exceeded.`),
          API__EP__STORY__PLAN_SUBSCRIPTION_ENDED: i18n._(t`Your subscription ended.`),
          API__EP__STORY__POST_LIMIT_HIT: i18n._(t`Scheduling limit exceeded.`),
          /**
           * ep.uploads
           */
          API__EP__UPLOADS__UPLOAD_SIZE_LIMIT_HIT: i18n._(t`Upload size limit exceed`),
          /**
           * ep.users
           */
          API__EP__USERS__AUTHENTICATION_ERROR: i18n._(t`User not found or password is incorrect.`),
          API__EP__USERS__AUTHORIZATION_ERROR: i18n._(t`User is not authenticated, action has been forbidden.`),
          API__EP__USERS__CODE_INVALID: i18n._(t`Code verification failed.`),
          API__EP__USERS__CODE_PROCESS_THROTTLED: i18n._(
            t`Code already sent. New code will be available in ${payload.body.extra?.wait}s`,
          ),
          API__EP__USERS__CODE_SEND_THROTTLED: i18n._(
            t`Code already sent. New code will be available in ${payload.body.extra?.wait}s`,
          ),
          API__EP__USERS__EMAIL_ALREADY_VERIFIED: i18n._(t`E-mail address has already been verified.`),
          API__EP__USERS__EMAIL_NOT_FOUND: i18n._(t`No account found with that e-mail address.`),
          API__EP__USERS__PASSWORD_INVALID: i18n._(t`Password is invalid.`),
          API__EP__USERS__PASSWORD_UNCHANGED: i18n._(t`Password matches your old password.`),
          API__EP__USERS__RECAPTCHA_INVALID_INPUT_RESPONSE: i18n._(t`The response parameter is invalid or malformed.`),
          API__EP__USERS__RECAPTCHA_INVALID_INPUT_SECRET: i18n._(t`The secret parameter is invalid or malformed.`),
          API__EP__USERS__RECAPTCHA_INVALID_OR_MALFORMED: i18n._(t`The request is invalid or malformed.`),
          API__EP__USERS__RECAPTCHA_LOW_SCORE: i18n._(t`ReCaptcha score is too low.`),
          API__EP__USERS__RECAPTCHA_TIMEOUT_OR_DUPLICATE: i18n._(
            t`The response parameter has timed out or has already been used.`,
          ),
          API__EP__USERS__THROTTLED: i18n._(t`Expected to be accessible in ${payload.body.extra?.wait} seconds.`),
          API__EP__USERS__UNKNOWN: i18n._(t`An unknown error has occurred.`),
          /**
           * internal
           */
          API__INTERNAL__400_BAD_REQUEST: i18n._(t`Something went wrong... Try again.`),
          API__INTERNAL__422_UNPROCESSABLE_ENTITY:
            payload?.body?.message || i18n._(t`Something went wrong... Try again.`),
          API__INTERNAL__401_UNAUTHORIZED: i18n._(t`Unauthorized`),
          API__INTERNAL__403_FORBIDDEN: i18n._(t`Forbidden`),
          API__INTERNAL__404_NOT_FOUND: i18n._(t`Not found`),
          API__INTERNAL__405_METHOD_NOT_ALLOWED: i18n._(t`Method not allowed`),
          API__INTERNAL__409_CONFLICT: i18n._(
            t`Your request could not be completed due to a conflict with the current server state`,
          ),
          API__INTERNAL__413_PAYLOAD_TOO_LARGE: i18n._(t`File exceeds the limit`),
          API__INTERNAL__500_INTERNAL_SERVER_ERROR: i18n._(t`Internal server error`),
          API__INTERNAL__501_NOT_IMPLEMENTED: i18n._(t`Not implemented`),
          API__INTERNAL__502_BAD_GATEWAY: i18n._(t`Bad gateway`),
          API__INTERNAL__503_SERVICE_UNAVAILABLE: i18n._(t`Service unavailable`),
          API__INTERNAL__504_GATEWAY_TIMEOUT: i18n._(t`Gateway timeout`),
          API__INTERNAL__UNKNOWN: i18n._(t`Unknown API error`),
          /**
           * root
           */
          API__ROOT__ABORT_ERROR: t`Request aborted.`,
          API__ROOT__FETCH__NO__RESPONSE: i18n._(
            t`Request error. Check your internet connection and local settings of anti-malware software.`,
          ),
          API__ROOT__ORGANIZATION_NOT_FOUND: i18n._(t`Organization not found`),
          API__ROOT__ORGANIZATION_TEMPORARILY_INACTIVE: i18n._(t`Organization is temporarily inactive`),
          API__ROOT__TRANSFER_PAGE_CONFIG_ID_REQUIRED: i18n._(
            t`«transferPageConfigId» query parameter must be set. If you don't need to move posts, just leave it blank`,
          ),
          API__ROOT__TRANSFER_PAGE_CONFIG_INVALID: i18n._(
            t`«transferPageConfigId» query parameter must be blank or a point to the same-page Page Config as the original, but and not the same Page Config as the original.`,
          ),
          API__ROOT__UNKNOWN: i18n._(t`Unknown error has occurred.`),
          API__ROOT__VALIDATION_ERROR: i18n._(t`Validation error.`),
          API__ROOT__RATE_LIMIT: i18n._(t`Rate limit exceeded.`),
          /**
           * smp.gpt.edit
           */
          API__SMP__GPT__EDIT__GPT_POLICY_VIOLATION: i18n._(t`The request was rejected by the security system.`),
          /**
           * smp.client-instagram
           */
          API__SMP__CLIENT_INSTAGRAM__BAD_LOGIN: i18n._(t`Invalid username or password.`),
          API__SMP__CLIENT_INSTAGRAM__BAD_LOGIN_AGAIN: i18n._(
            t`Invalid username/password. If you are sure they are valid, please try to log in to your account using a mobile device or a browser. Instagram could have reset your password for security reasons. Resetting your password will most often solve the issue.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__CHECKPOINT_ME_OR_NOT: i18n._(
            t`Instagram requires a log-in confirmation. Please log in to your account using a mobile device or a browser before using it.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__CODE_INVALID: i18n._(t`Code must contain 6 or 8 digits.`),
          API__SMP__CLIENT_INSTAGRAM__CODE_MISMATCH: i18n._(t`Confirmation code is incorrect.`),
          API__SMP__CLIENT_INSTAGRAM__CODE_RESEND_LIMIT_EXCEEDED: i18n._(
            t`Max. verification attempts exceeded, please try again later`,
          ),
          API__SMP__CLIENT_INSTAGRAM__CODE_RESEND_TIMEOUT: i18n._(
            t`Please wait a moment. We will send you a new code after ${payload.body.extra?.timeout} sec`,
          ),
          API__SMP__CLIENT_INSTAGRAM__CODE_SEND_CREDENTIALS_NOT_FOUND: i18n._(
            t`Verification credentials not found. Please ensure that the verified e-mail address and phone number are attached to your Instagram account.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__CODE_SEND_METHOD_UNKNOWN: i18n._(t`Code error: Unknown sending method.`),
          API__SMP__CLIENT_INSTAGRAM__LOGGED_OUT: i18n._(
            t`Instagram requires you to re-sign in. If after three attempts your Instagram account still fails to connect, please contact our technical support team.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__NETWORK_ERROR: i18n._(t`Network connection error.`),
          API__SMP__CLIENT_INSTAGRAM__PHONE_NUMBER_REQUIRED: i18n._(
            t`Your Instagram account requires a phone number. Please log in to your account using a mobile device or a browser, to add and confirm your phone number.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__RATE_LIMIT_EXCEEDED: i18n._(
            t`Limit exceeded. Please wait a few minutes, and try again.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__SENTRY_BLOCK: i18n._(
            t`Failed to login: Instagram has blocked new log-in attempts to your account. If after three attempts your Instagram account still fails to connect, please contact our technical support team.`,
          ),
          API__SMP__CLIENT_INSTAGRAM__UNKNOWN: i18n._(t`Unknown error.`),

          API__SMP__CLIENT_INSTAGRAM__USERNAME_LOGIN_REQUIRED: i18n._(
            t`Please use your username to log in (not your e-mail address).`,
          ),
          API__SMP__CLIENT_INSTAGRAM__USERNAME_LOGIN_REQUIRED_TWO_FACTOR: i18n._(
            t`Please ensure that you are using your username, rather than your e-mail address or phone number when logging in. Alternatively, you can disable Two-Factor Authentication while you're connecting your account.`,
          ),
          /**
           * smp.client-telegram
           */
          API__SMP__CLIENT_TELEGRAM__API_UNAVAILABLE: i18n._(t`Telegram API is unavailable. Please try again later.`),
          API__SMP__CLIENT_TELEGRAM__BOT_TOKEN_INVALID: i18n._(t`Invalid Bot Token.`),
          API__SMP__CLIENT_TELEGRAM__CHANNEL_NOT_FOUND: i18n._(t`Channel not found.`),
          API__SMP__CLIENT_TELEGRAM__CHANNEL_NOT_PUBLIC: i18n._(t`Only public channels allowed.`),
          API__SMP__CLIENT_TELEGRAM__EMPTY: i18n._(t`This list cannot be blank.`),
          API__SMP__CLIENT_TELEGRAM__NOT_CHAT_MEMBER: i18n._(t`Bot is not a member of the channel chat.`),
          API__SMP__CLIENT_TELEGRAM__NO_PUBLISH_PERMISSION: i18n._(t`Bot is not authorized to send a message.`),
          API__SMP__CLIENT_TELEGRAM__PUBLIC_CHAT_NOT_FOUND: i18n._(t`Public chat group not found.`),
          API__SMP__CLIENT_TELEGRAM__RATE_LIMIT_EXCEEDED: i18n._(
            t`Limit exceeded. Please wait a few minutes, and try again.`,
          ),
          API__SMP__CLIENT_TELEGRAM__UNKNOWN: i18n._(t`Unknown error.`),
          /**
           * smp.client-viber
           */
          API__SMP__CLIENT_VIBER__ACCOUNT_BLOCKED: i18n._(t`Public account is blocked.`),
          API__SMP__CLIENT_VIBER__ACCOUNT_NOT_PUBLIC: i18n._(t`The account associated with the token is not public.`),
          API__SMP__CLIENT_VIBER__API_UNAVAILABLE: i18n._(t`Viber API is unavailable. Please try again later.`),
          API__SMP__CLIENT_VIBER__AUTH_TOKEN_INVALID: i18n._(t`Authentication token is invalid.`),
          API__SMP__CLIENT_VIBER__UNKNOWN: i18n._(t`Unknown error.`),
          /**
           * smp.client-telegram-mtproto
           */
          API__SMP__CLIENT_TELEGRAM_MTPROTO__TWO_FACTOR_AUTH_REQUIRED: payload.body.message,
          API__SMP__CLIENT_TELEGRAM_MTPROTO__PHONE_CODE_INVALID: i18n._(t`The phone code provided is invalid.`),
          API__SMP__CLIENT_TELEGRAM_MTPROTO__PASSWORD_HASH_INVALID: i18n._(t`Two-factor password is invalid.`),
          /**
           * smp.repost
           */
          API__SMP__REPOST__REPOST_CONFIG__CYCLICAL_REPOST_CONFIG: i18n._(t`Source and destination must not match`),
          API__SMP__REPOST__REPOST_CONFIG__DESTINATION_MEDIA_PAGES_LIMIT_EXCEEDED: i18n._(
            t`Destination page limit reached`,
          ),
          API__SMP__REPOST__REPOST_CONFIG__DUPLICATED_REPOST_CONFIG: i18n._(t`Reposter configurations are duplicated`),
          API__SMP__REPOST__REPOST_CONFIG__INVALID: i18n._(t`Invalid value`),
          API__SMP__REPOST__REPOST_CONFIG__MEDIA_PAGES_LIMIT_EXCEEDED: i18n._(t`Page limit reached`),
          API__SMP__REPOST__REPOST_CONFIG__NAME_IS_NOT_UNIQUE: i18n._(t`Name is not unique`),
          API__SMP__REPOST__REPOST_CONFIG__NO_ACCOUNT_CREDENTIALS: i18n._(t`No account credentials were provided`),
          API__SMP__REPOST__REPOST_CONFIG__NO_CONTENT_SETTING_SELECTED: i18n._(
            t`At least one content setting must be enabled`,
          ),
          API__SMP__REPOST__REPOST_CONFIG__NO_PERMISSION_TO_POST: i18n._(t`No posting permissions granted`),
          API__SMP__REPOST__REPOST_CONFIG__NO_WEBHOOK_SETTINGS: i18n._(t`No webhook settings found`),
          API__SMP__REPOST__REPOST_CONFIG__REPOST_CONFIGS_LIMIT_EXCEEDED: i18n._(t`Reposter limit exceeded`),
          API__SMP__REPOST__REPOST_CONFIG__REPOST_HISTORY__FEATURE_NOT_EXIST: i18n._(t`Feature not exist`),
          /**
           * smp.stream
           */
          API__SMP__STREAM__DESCHEDULE_FORBIDDEN: i18n._(t`Forbidden to convert stream from scheduled to manual.`),
          API__SMP__STREAM__DESTINATION_ALREADY_EXISTS: i18n._(
            t`This channel is already in use by the current live stream.`,
          ),
          API__SMP__STREAM__DESTINATION_NAME_ALREADY_EXISTS: i18n._(t`Channel name already exists`),
          API__SMP__STREAM__DESTINATION_NOT_SPECIFIED: i18n._(
            t`Neither «medium:external_id» or «url» have been specified.`,
          ),
          API__SMP__STREAM__DESTINATION_TWICE_SPECIFIED: i18n._(
            t`Both «medium:external_id» and «url» are specified. Only one is required.`,
          ),
          API__SMP__STREAM__DESTINATION_URL_NOT_VALID: i18n._(
            t`URL must be one of the valid URL types (${(() => {
              const schemes = (payload.body.extra?.schemes as unknown as string[]) || [];
              return schemes.join(", ");
            })()})`,
          ),
          API__SMP__STREAM__INSUFFICIENT_RESOURCES: i18n._(t`Not enough resources.`),
          API__SMP__STREAM__LIMIT_HIT_AIR_TIME: i18n._(t`Exceeding the airtime limit.`),
          API__SMP__STREAM__LIMIT_HIT_LIVE_STREAMS_CONFIGURED: i18n._(
            t`Sorry, you have reached the limit of live-stream configs.`,
          ),
          API__SMP__STREAM__LIVE_STREAM_SOURCE_NOT_SUPPORTED: i18n._(t`Live Stream cannot have a source.`),
          API__SMP__STREAM__OBJECT_NOT_FOUND: i18n._(t`Object does not exist or is unavailable.`),
          API__SMP__STREAM__SCHEDULE_FORBIDDEN: i18n._(t`Forbidden to convert stream from manual to scheduled.`),
          API__SMP__STREAM__SOURCE_ORIGIN_INVALID: i18n._(
            t`You can only set «source» and «source_type» fields together.`,
          ),
          API__SMP__STREAM__SOURCE_POSITION_ALREADY_EXISTS: i18n._(t`Source position already exists.`),
          API__SMP__STREAM__STREAM_FORBIDDEN: i18n._(t`Streaming is not allowed.`),
          API__SMP__STREAM__STREAM_NAME_ALREADY_EXISTS: i18n._(t`Stream name already exists.`),
          API__SMP__STREAM__STREAM_START_TOO_CLOSE: i18n._(
            t`Stream starts no sooner than in ${payload.body.extra?.min_offset_s}s.`,
          ),
          API__EP__SAUTH__CODE_SEND_THROTTLED: payload.body.message,
          /**
           * Validation extra
           */
          API__ROOT__VALIDATION_ERROR__MAX_LENGTH: payload.body.extra?.MAX_LENGTH
            ? i18n._(t`Max. length is ${payload.body.extra?.MAX_LENGTH} characters`)
            : i18n._(t`The limit has been exceeded`),
          API__ROOT__VALIDATION_ERROR__PHONE_NUMBER_INVALID: i18n._(t`Incorrect phone number format`),
        };

        return (
          getPreferedApiError(failure.payload) ||
          messages[code] ||
          payload?.body?.message ||
          i18n._(t`Unknown api error`)
        );
      }

      if (failure instanceof Failure) {
        const messages: Record<IFailureCodes, string> = {
          OAUTH__INVALID_ELAMA_PLAN_SUBSCRIPTION: t`Your tariff plan is invalid. Contact our support team.`,
          ROOT__AVATAR_LOADING_FAIL: i18n._(t`Error uploading avatar. You can post the Story without an avatar.`),
          ROOT__BROWSER__SAFARI_NOT_SUPPORTED: i18n._(t`Safari is not supported by this module`),
          ROOT__CANVA__API_KEY_NOT_PROVIDED: i18n._(t`Missing API key for Canva`),
          ROOT__CRELLO__API_KEY_NOT_PROVIDED: i18n._(t`Missing API key for Crello`),
          ROOT__FETCH_FAILED: i18n._(t`Fetch failed`),
          ROOT__FETCH_NEXT_PARAM_DECODE_FAILED: i18n._(t`Fetch failed`),
          ROOT__FILE_MAX_SIZE_EXCEEDED: i18n._(t`Max. file size exceeded`),
          ROOT__FILE_TYPE_IS_NOT_SUPPORTED: i18n._(t`File format is not supported`),
          ROOT__IMAGE_IS_NOT_PROCESSABLE: i18n._(
            t`There was an error while processing the image. As the file cannot be opened, it will be removed from the Story.`,
          ),
          ROOT__IMAGE_IS_PROCESSING: i18n._(t`Error loading image. Try to reload this page later.`),
          ROOT__IMAGE_TYPE_IS_NOT_SUPPORTED: i18n._(
            t`Unsupported image format. The following image formats are supported: JPEG, PNG.`,
          ),
          ROOT__MEDIA_BROWSER_CANT_READ_VIDEO_SIZE: i18n._(
            t`Browser cannot read the width and height of the uploaded video.`,
          ),
          ROOT__MEDIA_FORMAT_IS_NOT_SUPPORTED: i18n._(t`Format of provided media is not supported by your browser`),
          ROOT__MEDIA_IS_NOT_PROCESSABLE: i18n._(
            t`There was an error while processing the video. As the file cannot be opened, it will be removed from the Story.`,
          ),
          ROOT__PROJECTS_NO_ACCESS_TO_EDIT: i18n._(t`Insufficient permissions`),
          ROOT__RECAPTCHA_INTERNAL_ERROR: i18n._(t`Recaptcha internal error`),
          ROOT__STORY__NO_ACCOUNTS: i18n._(t`No accounts connected`),
          ROOT__TOO_MANY_LOCATION_CHANGES: i18n._(t`Too much traffic`),
          ROOT__UNKNOWN: i18n._(t`Unknown internal error`),
          ROOT__WATERMARK_CANT_DECODE: i18n._(t`Error decoding watermark`),
          ROOT__WATERMARK_IMAGE_ELEMENT_NOT_DEFINED: i18n._(t`Error saving watermark image. Please try again later.`),
          ROOT__WATERMARK_IMAGE_IS_NOT_PROCESSABLE: i18n._(
            t`The watermark image is unavailable at this moment. Please try again later.`,
          ),
          ROOT__WATERMARK_IMAGE_IS_PROCESSING: i18n._(
            t`The watermark is in the progress of being created. Please try again later.`,
          ),
          ROOT__WATERMARK_NO_EMPTY_TEXT: i18n._(t`Error saving watermark. No text found.`),
          ROOT__WATERMARK_TEXT_ELEMENT_OR_POSITION_NOT_DEFINED: i18n._(
            t`Error saving watermark. Please try again later.`,
          ),
          ROOT__WATERMARK_TEXT_LENGTH_EXCEEDED: i18n._(t`Watermark character limit exceeded`),
          ROOT__CANVA__API_UNKNOWN: t`Something went wrong... Try again.`,
        };

        return messages[failure.code] || i18n._(t`Unknown internal error`);
      }

      if (failure instanceof Error) {
        if (isGqlError(failure)) {
          const error = failure.response.errors[0];

          const knownErrorByCode = {
            VALIDATION_ERROR: t`Validation error.`,
            FORBIDDEN: t`Forbidden`,
          }[error.error_type];

          return knownErrorByCode || error.extra?.message || error.message || t`Unknown error`;
        }

        return failure.message || i18n._(t`Unknown error`);
      }

      return i18n._(t`Unknown error`);
    },
    [i18n],
  );

  // TODO: return failure.payload.message if it possible

  return useMemo(() => ({ failureTranslationGet }), [failureTranslationGet]);
};
